/**
 * VariableConfigPopover - configures where one prompt {variable} gets its data from.
 *
 * Opened by clicking a variable token in the prompt editor (or its row in the variable list).
 * Replaces the old category -> field -> scope-name form with three plain questions: where does
 * the data come from, which fields, and (for item sets) what the rendered value looks like.
 * Several fields of the same item set can be combined into one variable - the backend renders
 * those item by item (see _resolve_context_field_sources in workflow_executor.py).
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ContextField, ContextFieldType, ContextFieldTypeInfo } from '../types/agent';
import { RAW_RESPONSE_FIELD, describePath, type ConnectedInputInfo, type UpstreamStep } from '../utils/outputPath';
import { CheckIcon, CloseIcon, TrashIcon } from './icons';
import { OutputPathPicker, useAgentOutputSchema } from './OutputPathPicker';
import './VariableConfigPopover.css';

export type SourceKind = 'item' | 'item_relations' | 'scope' | 'schema' | 'results' | 'config' | 'computed';

const RELATIONS = [
  { id: 'parent', label: 'Parent', many: false },
  { id: 'siblings', label: 'Siblings', many: true },
  { id: 'children', label: 'Children', many: true },
  { id: 'upstream', label: 'Traces up', many: true },
  { id: 'downstream', label: 'Traces down', many: true },
] as const;

const KINDS: Array<{ id: SourceKind; title: string; hint: string }> = [
  { id: 'item', title: 'Single item', hint: 'The item this step is processing' },
  { id: 'item_relations', title: 'Related items', hint: 'Parent, siblings, children or traced items' },
  { id: 'scope', title: 'All items of an input', hint: 'Every item of a connected input - also on a per-item step' },
  { id: 'schema', title: 'Schema or outline', hint: "A tracker's fields, or an outline input's tree" },
  { id: 'results', title: 'Previous step', hint: 'Output of an upstream agent' },
  { id: 'config', title: 'Configuration', hint: 'System settings and rulesets' },
  { id: 'computed', title: 'Advanced', hint: 'Runtime values (content id, hashes)' },
];

// Kinds whose fields all belong to one item set, so several can be combined into one variable.
const MULTI_FIELD_KINDS: SourceKind[] = ['item', 'item_relations', 'scope'];

// scope.<name>.fields/.json/.outline/.trackers render a tracker's schema / an outline / a tracker list, not items -
// they're the 'schema' kind and can't be combined.
const SCOPE_SCHEMA_FIELDS = ['fields', 'json', 'outline', 'trackers'];
const SCHEMA_FIELD_OPTIONS: FieldOption[] = [
  { value: 'fields', label: 'Tracker fields', description: 'One line per visible tracker field, with its choice options' },
  { value: 'json', label: 'Raw JSON', description: 'The raw tracker field definitions (or outline tree) as JSON' },
  { value: 'outline', label: 'Outline tree', description: 'Outline inputs: an indented "- summary [id]" line per node' },
  { value: 'trackers', label: 'Tracker list', description: 'One "- [id] name" line per tracker (Tracker List inputs)' },
];
// The schema fields each data source renders; item inputs (a tracker) can render their tracker's schema.
const SCHEMA_FIELDS_BY_TASK_TYPE: Record<string, string[]> = {
  data_source_outline: ['outline', 'json'],
  data_source_tracker_list: ['trackers', 'json'],
};
const ITEM_INPUT_SCHEMA_FIELDS = ['fields', 'json'];

function schemaFieldOptionsFor(input: ConnectedInputInfo | undefined): FieldOption[] {
  if (!input?.taskType) return SCHEMA_FIELD_OPTIONS;
  const allowed = SCHEMA_FIELDS_BY_TASK_TYPE[input.taskType] ?? ITEM_INPUT_SCHEMA_FIELDS;
  return SCHEMA_FIELD_OPTIONS.filter(o => allowed.includes(o.value));
}

// Superseded by Related items > Traces up/down, which can pick exactly the fields wanted.
const HIDDEN_COMPUTED = ['computed.upstream_references', 'computed.downstream_references'];

const DEFAULT_SCOPE_NAME = 'input';

interface FieldOption {
  value: string; // field name for item-set kinds, full source path otherwise
  label: string;
  description: string;
}

export interface VariableSelection {
  kind: SourceKind | null;
  relation: string;
  scopeName: string;
  fields: string[];
}

export function contextFieldSources(field: ContextField): string[] {
  if (field.sources?.length) return field.sources;
  return field.source ? [field.source] : [];
}

/** Parse a stored context field back into the popover's selection model. */
export function parseSelection(field: ContextField): VariableSelection {
  const sources = contextFieldSources(field);
  const empty: VariableSelection = { kind: null, relation: 'parent', scopeName: DEFAULT_SCOPE_NAME, fields: [] };
  if (!sources.length) return empty;

  const parts = sources[0].split('.');
  const head = parts[0];
  if (head === 'item' && parts.length === 3 && RELATIONS.some(r => r.id === parts[1])) {
    return { ...empty, kind: 'item_relations', relation: parts[1], fields: sources.map(s => s.split('.')[2]) };
  }
  if (head === 'computed' && HIDDEN_COMPUTED.includes(sources[0])) {
    // Legacy whole-content reference field - shown as the equivalent related-items selection.
    return { ...empty, kind: 'item_relations', relation: parts[1].split('_')[0], fields: ['summary', 'description'] };
  }
  if (head === 'item') {
    return { ...empty, kind: 'item', scopeName: '', fields: sources.map(s => s.split('.').slice(1).join('.')) };
  }
  if (head === 'single') {
    return { ...empty, kind: 'item', scopeName: parts[1] || '', fields: sources.map(s => s.split('.').slice(2).join('.')) };
  }
  if (head === 'scope') {
    const fields = sources.map(s => s.split('.').slice(2).join('.'));
    const kind = fields.every(f => SCOPE_SCHEMA_FIELDS.includes(f)) ? 'schema' : 'scope';
    return { ...empty, kind, scopeName: parts[1] || DEFAULT_SCOPE_NAME, fields };
  }
  if (head === 'results' || head === 'config' || head === 'computed') {
    return { ...empty, kind: head, fields: [sources[0]] };
  }
  return empty;
}

/** The context field `sources` a selection resolves to, in render order. */
export function selectionSources(selection: VariableSelection): string[] {
  const { kind, relation, scopeName, fields } = selection;
  switch (kind) {
    case 'item':
      // A chosen input (several connected item inputs) reads that input's current item;
      // otherwise item.* is the workflow's single iterated item.
      return scopeName.trim() ? fields.map(f => `single.${scopeName.trim()}.${f}`) : fields.map(f => `item.${f}`);
    case 'item_relations':
      return fields.map(f => `item.${relation}.${f}`);
    case 'scope':
    case 'schema':
      return fields.map(f => `scope.${scopeName.trim() || DEFAULT_SCOPE_NAME}.${f}`);
    case 'results':
    case 'config':
    case 'computed':
      return fields.slice(0, 1);
    default:
      return [];
  }
}

function itemFieldOptions(categories: ContextFieldTypeInfo[]): FieldOption[] {
  const fields = categories.find(c => c.id === 'item')?.fields ?? [];
  const options = fields.map(f => ({ value: f.id.replace(/^item\./, ''), label: f.name, description: f.description }));
  // Always offer the fields every item has, even if the backend list failed to load.
  for (const [value, label] of [['summary', 'Summary'], ['description', 'Description'], ['id', 'Item ID']]) {
    if (!options.some(o => o.value === value)) options.push({ value, label, description: '' });
  }
  return options;
}

function fieldOptionsFor(
  kind: SourceKind,
  categories: ContextFieldTypeInfo[],
  schemaInput?: ConnectedInputInfo
): FieldOption[] {
  if (kind === 'item' || kind === 'item_relations') return itemFieldOptions(categories);
  if (kind === 'schema') return schemaFieldOptionsFor(schemaInput);
  const fields = categories.find(c => c.id === kind)?.fields ?? [];
  if (kind === 'scope') {
    return fields
      .map(f => ({ value: f.id.split('.').slice(2).join('.'), label: f.name, description: f.description }))
      .filter(o => !SCOPE_SCHEMA_FIELDS.includes(o.value));
  }
  return fields
    .filter(f => kind !== 'computed' || !HIDDEN_COMPUTED.includes(f.id))
    .map(f => ({ value: f.id, label: f.name, description: f.description }));
}

// The UI's generic "Previous Agent Result" - the backend resolves an unknown step name to
// the current step's first dependency (see _resolve_step_output_reference).
const GENERIC_PREVIOUS_STEP = 'output';

/** "results.<step>.<path>" -> its step name and the path within that step's output. */
export function parseResultsSource(source: string | undefined): { step: string; path: string } {
  const [, step = GENERIC_PREVIOUS_STEP, ...rest] = (source ?? '').split('.');
  return { step, path: rest.join('.') };
}

function resultsSource(step: string, path: string): string {
  return ['results', step || GENERIC_PREVIOUS_STEP, path].filter(Boolean).join('.');
}

interface ResultsFieldEditorProps {
  source: string | undefined;
  upstreamSteps: UpstreamStep[];
  onChange: (source: string) => void;
}

/** Picks which upstream step, and which field / list item of its output, a variable reads. */
const ResultsFieldEditor: React.FC<ResultsFieldEditorProps> = ({ source, upstreamSteps, onChange }) => {
  const { step, path } = parseResultsSource(source);
  // A lone upstream step is what the generic reference resolves to anyway - name it explicitly.
  const effectiveStep = step === GENERIC_PREVIOUS_STEP && upstreamSteps.length === 1 ? upstreamSteps[0].stepName : step;
  const upstream = upstreamSteps.find(u => u.stepName === effectiveStep);
  const { schema, loading } = useAgentOutputSchema(upstream?.agentId);

  return (
    <>
      {upstreamSteps.length > 1 && (
        <>
          <div className="variable-popover__step">Which step?</div>
          <select
            className="variable-popover__text"
            value={upstream ? effectiveStep : ''}
            onChange={(e) => onChange(resultsSource(e.target.value, ''))}
          >
            <option value="">Select a step…</option>
            {upstreamSteps.map(u => (
              <option key={u.stepName} value={u.stepName}>{u.label}</option>
            ))}
          </select>
        </>
      )}
      <div className="variable-popover__step">Which value?</div>
      <OutputPathPicker
        value={path}
        onChange={(next) => onChange(resultsSource(effectiveStep, next))}
        schema={schema}
        loading={loading}
        placeholder={`${RAW_RESPONSE_FIELD} (full output) or e.g. issues.2.title`}
      />
    </>
  );
};

/** Short human summary of a field's data source, for the variable list. */
export function describeSelection(selection: VariableSelection, categories: ContextFieldTypeInfo[]): string | null {
  if (!selection.kind || !selection.fields.length) return null;
  const options = fieldOptionsFor(selection.kind, categories);
  const labels = selection.fields.map(f => options.find(o => o.value === f)?.label ?? f).join(' + ');
  switch (selection.kind) {
    case 'item':
      return selection.scopeName ? `Single item of "${selection.scopeName}" · ${labels}` : `Single item · ${labels}`;
    case 'item_relations':
      return `${RELATIONS.find(r => r.id === selection.relation)?.label ?? selection.relation} · ${labels}`;
    case 'scope':
      return `All items of "${selection.scopeName}" · ${labels}`;
    case 'schema':
      return `Schema of "${selection.scopeName}" · ${labels}`;
    case 'results': {
      const { step, path } = parseResultsSource(selection.fields[0]);
      const stepLabel = step === GENERIC_PREVIOUS_STEP ? 'Previous step' : step;
      return `${stepLabel} · ${!path || path === RAW_RESPONSE_FIELD ? 'full output' : describePath(path)}`;
    }
    default:
      return `${KINDS.find(k => k.id === selection.kind)?.title} · ${labels}`;
  }
}

/** A sketch of what the variable renders to, so the shape (one block per item) is visible up front. */
function previewText(selection: VariableSelection, labelOf: (f: string) => string): string {
  const { kind, fields, relation } = selection;
  if (!kind || !fields.length) return '';
  if (kind === 'results') {
    const { path } = parseResultsSource(fields[0]);
    return `‹${!path || path === RAW_RESPONSE_FIELD ? 'previous step output' : describePath(path)}›`;
  }
  if (!MULTI_FIELD_KINDS.includes(kind)) return `‹${labelOf(fields[0])}›`;

  const flat = (withId: boolean) =>
    fields.length === 1
      ? `‹${labelOf(fields[0])}›`
      : fields.filter(f => withId || f !== 'id').map(f => `${f}: ‹${labelOf(f)}›`).join('\n');
  const block = (n: number) =>
    fields.length === 1
      ? `- [item ${n}] ‹${labelOf(fields[0])}›`
      : `- [item ${n}]\n` + fields.filter(f => f !== 'id').map(f => `  ${f}: ‹${labelOf(f)}›`).join('\n');
  const blocks = `${block(1)}\n${block(2)}\n…`;

  if (kind === 'item') return flat(true);
  if (kind === 'item_relations') return relation === 'parent' ? flat(true) : blocks;
  return blocks;
}

interface VariableConfigPopoverProps {
  field: ContextField;
  categories: ContextFieldTypeInfo[];
  // Set when the agent is edited from a workflow node: the steps feeding that node.
  upstreamSteps?: UpstreamStep[];
  // ...and the data sources wired to it.
  connectedInputs?: ConnectedInputInfo[];
  anchor: DOMRect;
  takenNames: Set<string>;
  onApply: (updates: Partial<ContextField> & { name: string }) => void;
  onRemove: () => void;
  onClose: () => void;
}

const POPOVER_WIDTH = 460;
const VIEWPORT_GUTTER = 16;

export const VariableConfigPopover: React.FC<VariableConfigPopoverProps> = ({
  field,
  categories,
  upstreamSteps,
  connectedInputs,
  anchor,
  takenNames,
  onApply,
  onRemove,
  onClose,
}) => {
  const [selection, setSelection] = useState<VariableSelection>(() => parseSelection(field));
  const [name, setName] = useState(field.name);
  const [description, setDescription] = useState(field.description || '');
  const [position, setPosition] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Anchor below the clicked token, flipping above it when there isn't room, clamped to the viewport.
  useLayoutEffect(() => {
    const el = popoverRef.current;
    if (!el) return;
    const height = el.offsetHeight;
    const width = Math.min(POPOVER_WIDTH, window.innerWidth - 2 * VIEWPORT_GUTTER);
    const left = Math.min(Math.max(anchor.left, VIEWPORT_GUTTER), window.innerWidth - width - VIEWPORT_GUTTER);
    const below = window.innerHeight - anchor.bottom - VIEWPORT_GUTTER;
    const above = anchor.top - VIEWPORT_GUTTER;
    if (height <= below || below >= above) {
      setPosition({ top: anchor.bottom + 6, left, maxHeight: below - 6 });
    } else {
      setPosition({ top: Math.max(VIEWPORT_GUTTER, anchor.top - 6 - Math.min(height, above)), left, maxHeight: above - 6 });
    }
  }, [anchor, selection.kind]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    const onPointer = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (popoverRef.current?.contains(target)) return;
      // Clicking another variable (token or list row) re-targets the popover rather than closing it -
      // this listener runs after that click's own handler has already opened the new one.
      if (target.closest?.('[data-variable-name], .variable-row')) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    // Deferred so the click that opened the popover doesn't immediately close it.
    const timer = setTimeout(() => document.addEventListener('mousedown', onPointer));
    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [onClose]);

  const schemaInput = connectedInputs?.find(i => i.name === selection.scopeName);
  const options = useMemo(
    () => (selection.kind ? fieldOptionsFor(selection.kind, categories, schemaInput) : []),
    [selection.kind, categories, schemaInput]
  );
  const labelOf = (f: string) => options.find(o => o.value === f)?.label ?? f;
  const multi = selection.kind !== null && MULTI_FIELD_KINDS.includes(selection.kind);

  const toggleField = (value: string) => {
    setSelection(prev => {
      if (!multi) return { ...prev, fields: [value] };
      if (prev.fields.includes(value)) return { ...prev, fields: prev.fields.filter(f => f !== value) };
      return { ...prev, fields: [...prev.fields, value] };
    });
  };

  // Inputs each kind can read: Single item / All items need items, Tracker schema needs a tracker
  // or schema/outline source. Undefined outside a workflow - the input name is then typed freely.
  const itemInputs = connectedInputs?.filter(i => !i.itemless);
  const inputsFor = (kind: SourceKind | null) =>
    kind === 'schema' ? connectedInputs : kind === 'item' || kind === 'scope' ? itemInputs : undefined;
  const defaultInputFor = (kind: SourceKind): string => {
    if (kind === 'item') return itemInputs && itemInputs.length > 1 ? itemInputs[0].name : '';
    const options = inputsFor(kind);
    return options?.length ? options[0].name : DEFAULT_SCOPE_NAME;
  };
  const kindInputs = inputsFor(selection.kind);

  const trimmedName = name.trim();
  const nameError = !/^\w+$/.test(trimmedName)
    ? 'Letters, digits and underscores only'
    : trimmedName !== field.name && takenNames.has(trimmedName)
      ? 'Another variable already has this name'
      : null;
  const sources = selectionSources(selection);
  const canApply = !nameError && sources.length > 0;

  const apply = () => {
    if (!canApply || !selection.kind) return;
    onApply({
      name: trimmedName,
      description,
      field_type: selection.kind as unknown as ContextFieldType,
      source: sources[0],
      sources: sources.length > 1 ? sources : undefined,
    });
  };

  const preview = previewText(selection, labelOf);

  return (
    <div
      ref={popoverRef}
      className="variable-popover"
      role="dialog"
      aria-label={`Configure {${field.name}}`}
      style={{
        top: position?.top ?? anchor.bottom + 6,
        left: position?.left ?? anchor.left,
        maxHeight: position?.maxHeight,
        visibility: position ? 'visible' : 'hidden',
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) apply();
      }}
    >
      <div className="variable-popover__header">
        <span className="variable-popover__brace">{'{'}</span>
        <input
          className="variable-popover__name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Variable name"
          spellCheck={false}
        />
        <span className="variable-popover__brace">{'}'}</span>
        <button className="variable-popover__icon-btn" onClick={onClose} title="Close (Esc)">
          <CloseIcon />
        </button>
      </div>
      {nameError && <div className="variable-popover__error">{nameError}</div>}

      <div className="variable-popover__body">
        <div className="variable-popover__step">Where does the data come from?</div>
        <div className="variable-popover__kinds">
          {KINDS.map(k => (
            <button
              key={k.id}
              className={`variable-popover__kind ${selection.kind === k.id ? 'is-active' : ''}`}
              onClick={() =>
                setSelection(prev =>
                  prev.kind === k.id ? prev : { ...prev, kind: k.id, fields: [], scopeName: defaultInputFor(k.id) }
                )
              }
            >
              <span className="variable-popover__kind-title">{k.title}</span>
              <span className="variable-popover__kind-hint">{k.hint}</span>
            </button>
          ))}
        </div>

        {selection.kind === 'item_relations' && (
          <>
            <div className="variable-popover__step">Which related items?</div>
            <div className="variable-popover__segmented">
              {RELATIONS.map(r => (
                <button
                  key={r.id}
                  className={selection.relation === r.id ? 'is-active' : ''}
                  onClick={() => setSelection(prev => ({ ...prev, relation: r.id }))}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </>
        )}

        {selection.kind === 'item' && (itemInputs ? itemInputs.length > 1 : !!selection.scopeName) && (
          <>
            <div className="variable-popover__step">Which input?</div>
            {itemInputs ? (
              <select
                className="variable-popover__text"
                value={selection.scopeName}
                onChange={(e) => setSelection(prev => ({ ...prev, scopeName: e.target.value }))}
              >
                {itemInputs.map(i => (
                  <option key={i.name} value={i.name}>{i.label} ({i.name})</option>
                ))}
              </select>
            ) : (
              <input
                className="variable-popover__text"
                value={selection.scopeName}
                onChange={(e) => setSelection(prev => ({ ...prev, scopeName: e.target.value }))}
                spellCheck={false}
              />
            )}
            <small className="variable-popover__note">
              The step reads the item being processed only when it comes from this input - the workflow iterates
              over every input read as a single item.
            </small>
          </>
        )}

        {(selection.kind === 'scope' || selection.kind === 'schema') && (
          <>
            <div className="variable-popover__step">Which input?</div>
            {kindInputs?.length ? (
              <select
                className="variable-popover__text"
                value={selection.scopeName}
                onChange={(e) => {
                  const scopeName = e.target.value;
                  setSelection(prev => {
                    if (prev.kind !== 'schema') return { ...prev, scopeName };
                    // Drop fields the newly picked input can't render (e.g. "Outline tree" on a tracker).
                    const allowed = schemaFieldOptionsFor(kindInputs?.find(i => i.name === scopeName)).map(o => o.value);
                    return { ...prev, scopeName, fields: prev.fields.filter(f => allowed.includes(f)) };
                  });
                }}
              >
                {kindInputs.some(i => i.name === selection.scopeName) ? null : (
                  <option value={selection.scopeName}>{selection.scopeName}</option>
                )}
                {kindInputs.map(i => (
                  <option key={i.name} value={i.name}>{i.label} ({i.name})</option>
                ))}
              </select>
            ) : (
              <>
                <input
                  className="variable-popover__text"
                  value={selection.scopeName}
                  onChange={(e) => setSelection(prev => ({ ...prev, scopeName: e.target.value }))}
                  placeholder={DEFAULT_SCOPE_NAME}
                  spellCheck={false}
                />
                <small className="variable-popover__note">
                  The Input Name of the data source node on the canvas. Leave as "{DEFAULT_SCOPE_NAME}" when the
                  pipeline has only one.
                </small>
              </>
            )}
            <small className="variable-popover__note">
              {selection.kind === 'scope'
                ? 'Every item of the input, even on a step that runs once per item. An input read only this way is used as context and not iterated over.'
                : "Works on a step that runs once per item too. A tracker's schema needs the tracker registered for migration."}
            </small>
          </>
        )}

        {selection.kind === 'results' && upstreamSteps?.length ? (
          <ResultsFieldEditor
            source={selection.fields[0]}
            upstreamSteps={upstreamSteps}
            onChange={(source) => setSelection(prev => ({ ...prev, fields: [source] }))}
          />
        ) : selection.kind && (
          <>
            <div className="variable-popover__step">
              {multi ? 'Which fields? (pick one or more)' : 'Which value?'}
            </div>
            <div className="variable-popover__fields">
              {options.length === 0 && <div className="variable-popover__note">Nothing available.</div>}
              {options.map(o => {
                const order = selection.fields.indexOf(o.value);
                return (
                  <button
                    key={o.value}
                    className={`variable-popover__field ${order !== -1 ? 'is-active' : ''}`}
                    onClick={() => toggleField(o.value)}
                    title={o.description}
                  >
                    <span className={`variable-popover__check ${multi ? '' : 'is-radio'}`}>
                      {order !== -1 && (multi && selection.fields.length > 1 ? order + 1 : <CheckIcon size={10} />)}
                    </span>
                    <span className="variable-popover__field-label">{o.label}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {preview && (
          <>
            <div className="variable-popover__step">Renders as</div>
            <pre className="variable-popover__preview">{preview}</pre>
            {selection.kind === 'item' && (
              <small className="variable-popover__note">
                On a step that runs once over all items, this repeats as one block per item.
              </small>
            )}
          </>
        )}

        <details className="variable-popover__more">
          <summary>Note for testers</summary>
          <input
            className="variable-popover__text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Shown as a hint when testing the agent in the sandbox"
          />
        </details>
      </div>

      <div className="variable-popover__footer">
        <button className="variable-popover__remove" onClick={onRemove} title="Remove this variable's configuration">
          <TrashIcon /> Remove
        </button>
        <div className="variable-popover__footer-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={apply} disabled={!canApply}>Apply</button>
        </div>
      </div>
    </div>
  );
};
